<template>
  <!--
    POST-UAT-UX / PUX-B (doc 35/01 §2) -- the existing Whiteboard, opened from
    the main Chat composer as the rendered V405 sheet
    (`.borna-whiteboard-sheet` / `-panel` / `-head` / `-close`; full screen
    up to 900px) over the chat. The chat route is not left: its session,
    stream, turns and the composer's unsent draft stay mounted behind it.

    The body is the PRODUCTION Board, not the prototype's canvas: the
    conversation's own persisted board (`aiTutorBoard`: load, autosave,
    stale, flush), `BoardToolbar`, `BoardEditor`, `BoardEquationDialog`,
    and the WB-M2A share -- «از AI Tutor بپرس» freezes a picture, the share
    sheet sends it through the ordinary `aiTutor/sendMessage` with the
    Borna-only share descriptor and, at most, the Hint / Explain action
    (never `check_answer`, never the Setup action -- the store's and
    Subway's own rules). Same orchestration as the Board Workspace's board
    half; the Workspace itself is not touched.

    The Board is keyed by the Borna conversation: the active session's is
    resolved first (`getSessionConversation`, PUX-S), so the Board also
    opens before the first exchange.

    Closing (×, the backdrop) saves the Board first; a Board that cannot be
    saved asks before it is left, as the Workspace does. Escape is the
    editor's (it leaves a tool / a selection), so it does not close the
    sheet -- V405 has no Escape either.
  -->
  <div
    class="chat-board-sheet"
    data-testid="ai-tutor-board-sheet"
    :data-sheet-phase="phase"
    @pointerdown="onSheetPointerDown"
    @click.self="onBackdropClick"
  >
    <section
      ref="panel"
      class="chat-board-sheet__panel"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
    >
      <header class="chat-board-sheet__head">
        <div class="chat-board-sheet__heading">
          <h3 :id="titleId">{{ $t('aiTutorBoard.sheet.title') }}</h3>
          <p>{{ $t('aiTutorBoard.sheet.subtitle') }}</p>
        </div>
        <span
          v-if="
            phase === 'board' &&
              boardStatus === 'ready' &&
              saveState !== 'clean'
          "
          class="chat-board-sheet__save"
          :class="`chat-board-sheet__save--${saveState}`"
          role="status"
          data-testid="ai-tutor-board-save-state"
          :data-save-state="saveState"
        >
          {{ saveLabel }}
        </span>
        <button
          ref="close"
          type="button"
          class="chat-board-sheet__close"
          data-testid="ai-tutor-board-sheet-close"
          :aria-label="$t('aiTutorBoard.sheet.close')"
          :title="$t('aiTutorBoard.sheet.close')"
          @click="requestClose"
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.9"
            stroke-linecap="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </header>

      <div
        v-if="phase !== 'board' || boardStatus !== 'ready'"
        class="chat-board-sheet__state"
        :role="stateBusy ? 'status' : 'alert'"
        data-testid="ai-tutor-board-state"
        :data-board-status="phase === 'board' ? boardStatus : phase"
      >
        <p>{{ stateText }}</p>
        <button
          v-if="!stateBusy"
          type="button"
          class="chat-board-sheet__button"
          data-testid="ai-tutor-board-load-retry"
          @click="init"
        >
          {{ $t('aiTutorBoard.states.retry') }}
        </button>
      </div>

      <template v-else>
        <board-toolbar
          :active-tool="activeTool"
          :disabled="!editorReady || readOnly || editorFailed"
          :view-disabled="!editorReady"
          share
          :share-disabled="shareDisabled"
          @tool="onTool"
          @equation="onEquationTool"
          @undo="onUndo"
          @redo="onRedo"
          @reset-view="onResetView"
          @share="onShareTool"
          :pen-style="penStyle"
          :clear-disabled="boardEmpty"
          @pen-style="onPenStyle"
          @clear="onClearBoard"
        />
        <div class="chat-board-sheet__stage">
          <board-editor
            ref="editor"
            :key="editorKey"
            :locale="locale"
            :initial-scene="initialScene"
            :read-only="readOnly"
            :pen-style="penStyle"
            @ready="onEditorReady"
            @change="onEditorChange"
            @equation-selection="onEquationSelection"
            @equation-edit="onEquationEdit"
            @chrome-leak="editorFailed = true"
          />
          <div
            v-if="emptyHintShown"
            class="chat-board-sheet__empty"
            data-testid="ai-tutor-board-empty"
          >
            <strong>{{ $t('aiTutorBoard.empty.title') }}</strong>
            <span>{{ $t('aiTutorBoard.empty.body') }}</span>
          </div>
        </div>
        <footer class="chat-board-sheet__foot">
          <!-- P40 QA-28: closing a Board that could not be saved asks HERE,
               in the sheet, never with the browser's own confirm dialog. -->
          <div
            v-if="leavePrompt"
            class="chat-board-sheet__notice chat-board-sheet__notice--leave"
            role="alertdialog"
            :aria-label="leavePromptText"
            data-testid="ai-tutor-board-leave-prompt"
            @keydown.esc.stop.prevent="onLeaveStay"
          >
            <span>{{ leavePromptText }}</span>
            <button
              ref="leaveStay"
              type="button"
              class="chat-board-sheet__button"
              data-testid="ai-tutor-board-leave-stay"
              @click="onLeaveStay"
            >
              {{ $t('aiTutorBoard.leave.stay') }}
            </button>
            <button
              type="button"
              class="chat-board-sheet__button"
              data-testid="ai-tutor-board-leave-anyway"
              @click="onLeaveAnyway"
            >
              {{ $t('aiTutorBoard.leave.anyway') }}
            </button>
          </div>
          <div
            v-else-if="saveState === 'stale'"
            class="chat-board-sheet__notice"
            role="alert"
            data-testid="ai-tutor-board-stale"
          >
            <span>{{ $t('aiTutorBoard.save.staleHint') }}</span>
            <button
              type="button"
              class="chat-board-sheet__button"
              data-testid="ai-tutor-board-reload"
              :disabled="reloading"
              @click="onReload"
            >
              {{ $t('aiTutorBoard.save.reload') }}
            </button>
          </div>
          <div
            v-else-if="saveState === 'failed'"
            class="chat-board-sheet__notice"
            role="alert"
            data-testid="ai-tutor-board-not-saved"
          >
            <span>{{ notSavedText }}</span>
            <button
              type="button"
              class="chat-board-sheet__button"
              data-testid="ai-tutor-board-save-retry"
              @click="onRetrySave"
            >
              {{ $t('aiTutorBoard.save.retry') }}
            </button>
          </div>
          <span
            v-else-if="editorFailed"
            class="chat-board-sheet__notice"
            role="alert"
            data-testid="ai-tutor-board-editor-failed"
          >
            {{ $t('aiTutorBoard.states.editorFailed') }}
          </span>
          <span v-else class="chat-board-sheet__hint">
            {{ $t('aiTutorBoard.hint') }}
          </span>
          <button
            v-if="equationEditable"
            type="button"
            class="chat-board-sheet__button chat-board-sheet__equation-edit"
            data-testid="ai-tutor-board-equation-edit"
            @click="onEquationEdit(selectedEquationId)"
          >
            {{ $t('aiTutorBoard.equation.editAction') }}
          </button>
        </footer>
      </template>
    </section>

    <board-equation-dialog
      v-if="equationDialog"
      :mode="equationDialog.mode"
      :source="equationDialog.source"
      :busy="equationDialog.busy"
      :problem="equationDialog.problem"
      @submit="onEquationSubmit"
      @edited="onEquationEdited"
      @cancel="closeEquationDialog"
    />
    <board-share-dialog
      v-if="shareDialog"
      :share="shareDialog.share"
      :busy="shareDialog.busy"
      :problem="shareDialog.problem || shareBlockedBy"
      @send="onShareSend"
      @cancel="closeShareDialog"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import BoardEditor from './BoardEditor.vue'
import BoardToolbar from './BoardToolbar.vue'
import { DEFAULT_PEN_STYLE } from '@/services/aiTutor/boardPen'
import BoardEquationDialog from './BoardEquationDialog.vue'
import BoardShareDialog from './BoardShareDialog.vue'
import { isOnline, onReconnect } from '@/services/connectivity'
import { collaborators as aiTutorCollaborators } from '@/store/modules/aiTutor'
import { freezeBoardShare } from '@/services/aiTutor/boardSnapshot'
import {
  boardShareRequest,
  toBoardShareDescriptor,
} from '@/services/aiTutor/boardShare'
import {
  createPreviewUrl,
  revokePreviewUrl,
} from '@/services/aiTutor/imageFile'

// The Board Workspace's own mappings, so the two hosts say the same things.
const EQUATION_PROBLEMS = {
  rtl_text_unsupported: 'rtl',
  engine_unavailable: 'unavailable',
}
const BOARD_STATE_KEYS = {
  idle: 'loading',
  loading: 'loading',
  unavailable: 'unavailable',
  not_found: 'notFound',
  forbidden: 'forbidden',
  not_active: 'notActive',
  error: 'error',
}

let instances = 0

export default {
  name: 'ChatBoardSheet',
  components: {
    BoardEditor,
    BoardToolbar,
    BoardEquationDialog,
    BoardShareDialog,
  },
  props: {
    // The chat's active session (`activeSetupContext.subject` + session id).
    courseName: {
      type: String,
      required: true,
    },
    sessionId: {
      type: String,
      required: true,
    },
    // Why the chat cannot take a share RIGHT NOW (the Workspace's reasons:
    // 'chat' | 'credits' | 'busy'), or null. Shown in the share sheet; the
    // frozen picture stays.
    blockedBy: {
      type: String,
      default: null,
    },
  },
  data() {
    instances += 1
    return {
      titleId: `ai-tutor-board-sheet-title-${instances}`,
      // 'resolving' (the conversation of the session) | 'board' | 'missing'
      phase: 'resolving',
      conversationId: '',
      initialScene: null,
      editorKey: 0,
      editorReady: false,
      editorFailed: false,
      activeTool: 'freedraw',
      // P41 BT-53: the pen settings of this board (the toolbar shows them, the
      // editor draws with them; they outlive a reloaded editor).
      penStyle: { ...DEFAULT_PEN_STYLE },
      boardEmpty: false,
      selectedEquationId: null,
      equationDialog: null,
      shareDialog: null,
      shareRendering: false,
      reloading: false,
      closing: false,
      // P40 QA-28: the in-sheet "leave without saving?" question.
      leavePrompt: false,
    }
  },
  computed: {
    ...mapState('aiTutorBoard', {
      boardStatus: 'status',
      saveState: 'saveState',
      saveError: 'saveError',
    }),
    locale() {
      return (this.$i18n && this.$i18n.locale) || 'fa'
    },
    readOnly() {
      return this.saveState === 'stale'
    },
    boardEditable() {
      return this.editorReady && !this.readOnly && !this.editorFailed
    },
    emptyHintShown() {
      return this.boardEditable && this.boardEmpty
    },
    equationEditable() {
      return this.boardEditable && !!this.selectedEquationId
    },
    shareDisabled() {
      return (
        !this.boardEditable ||
        this.boardEmpty ||
        this.shareRendering ||
        !!this.shareDialog ||
        !!this.equationDialog
      )
    },
    // P40 QA-28: offline the reason is named.
    leavePromptText() {
      return isOnline()
        ? this.$t('aiTutorBoard.leaveUnsaved')
        : this.$t('aiTutorBoard.leave.offline')
    },
    shareBlockedBy() {
      // P40 QA-30: offline, a share could only fail after the dialog closed
      // (behind this sheet), so it is not offered at all.
      if (!isOnline()) return 'offline'
      if (this.blockedBy) return this.blockedBy
      if (this.readOnly) return 'readOnly'
      return null
    },
    stateBusy() {
      if (this.phase === 'resolving') return true
      if (this.phase === 'missing') return false
      return this.boardStatus === 'loading' || this.boardStatus === 'idle'
    },
    stateText() {
      if (this.phase === 'resolving') {
        return this.$t('aiTutorBoard.states.loading')
      }
      if (this.phase === 'missing') {
        return this.$t('aiTutorBoard.states.unavailable')
      }
      return this.$t(
        `aiTutorBoard.states.${BOARD_STATE_KEYS[this.boardStatus] || 'error'}`
      )
    },
    saveLabel() {
      return this.$t(`aiTutorBoard.save.${this.saveState}`)
    },
    notSavedText() {
      const code = this.saveError && this.saveError.code
      if (code === 'AI_TUTOR_BOARD_SCENE_TOO_LARGE') {
        return this.$t('aiTutorBoard.save.tooLarge')
      }
      return this.$t('aiTutorBoard.save.failedHint')
    },
  },
  watch: {
    readOnly(now) {
      if (now && this.equationDialog) this.equationDialog.problem = 'readOnly'
      if (now && this.shareDialog) this.shareDialog.problem = 'stale'
    },
    // P40 QA-28: nothing is left to lose once a save went through.
    saveState(now) {
      if (this.leavePrompt && now === 'saved') this.leavePrompt = false
    },
  },
  created() {
    this.destroyed = false
    this.initSeq = 0
    this.previousOverflow = null
  },
  mounted() {
    // Over everything the chat route draws (header, composer, navigation).
    if (this.$el.parentNode !== document.body) {
      document.body.appendChild(this.$el)
    }
    this.previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('pagehide', this.onPageHide)
    document.addEventListener('focusin', this.onFocusIn)
    // P40 QA-28: a save that failed while offline is tried again as soon as
    // the browser is back online.
    this.offReconnect = onReconnect(this.onReconnect)
    this.$nextTick(() => {
      if (this.$refs.close) this.$refs.close.focus()
    })
    this.init()
  },
  beforeDestroy() {
    this.destroyed = true
    this.closeShareDialog()
    window.removeEventListener('pagehide', this.onPageHide)
    document.removeEventListener('focusin', this.onFocusIn)
    if (this.offReconnect) this.offReconnect()
    // Gone without the close flow (the route changed under it): the
    // best-effort keep-alive save, as on page hide, then the module is free.
    if (!this.closing) this.$store.dispatch('aiTutorBoard/flushOnPageHide')
    this.$store.dispatch('aiTutorBoard/reset')
    if (this.$el.parentNode) this.$el.parentNode.removeChild(this.$el)
  },
  // The page scroll comes back only AFTER the children are gone: the share
  // and equation dialogs restore, when they are destroyed, the `hidden` this
  // sheet had set when they opened -- restoring before them would leave the
  // chat unscrollable (focused review P1).
  destroyed() {
    document.body.style.overflow = this.previousOverflow || ''
  },
  methods: {
    // ---- conversation + board ---------------------------------------------
    async init() {
      this.initSeq += 1
      const seq = this.initSeq
      this.phase = 'resolving'
      this.initialScene = null
      this.editorReady = false
      this.editorFailed = false
      this.boardEmpty = false
      this.selectedEquationId = null
      this.equationDialog = null
      this.closeShareDialog()
      let conversationId = this.conversationId
      if (!conversationId) {
        try {
          const found = await aiTutorCollaborators.api.getSessionConversation(
            this.courseName,
            this.sessionId
          )
          conversationId =
            found && found.conversationId ? String(found.conversationId) : ''
        } catch (err) {
          conversationId = ''
        }
      }
      if (this.destroyed || seq !== this.initSeq) return
      if (!conversationId) {
        this.phase = 'missing'
        return
      }
      this.conversationId = conversationId
      this.phase = 'board'
      const result = await this.$store.dispatch(
        'aiTutorBoard/load',
        conversationId
      )
      if (this.destroyed || seq !== this.initSeq) return
      if (result && result.ok) this.mountScene(result.scene)
    },
    mountScene(scene) {
      this.initialScene = Object.freeze(scene)
      this.editorReady = false
      this.boardEmpty = false
      this.selectedEquationId = null
      this.equationDialog = null
      this.closeShareDialog()
      this.editorKey += 1
    },
    onEditorReady({ sceneVersion, activeTool, elementCount }) {
      this.editorReady = true
      this.activeTool = activeTool
      this.boardEmpty = elementCount === 0
      this.$store.dispatch('aiTutorBoard/markClean', sceneVersion)
    },
    onEditorChange({ sceneVersion, activeTool, elementCount }) {
      this.activeTool = activeTool
      this.boardEmpty = elementCount === 0
      this.$store.dispatch('aiTutorBoard/noteChange', {
        sceneVersion,
        getScene: () => (this.$refs.editor ? this.$refs.editor.getScene() : {}),
      })
    },
    onTool(tool) {
      if (this.$refs.editor) this.$refs.editor.setTool(tool)
    },
    onUndo() {
      if (this.$refs.editor) this.$refs.editor.undo()
    },
    onRedo() {
      if (this.$refs.editor) this.$refs.editor.redo()
    },
    onResetView() {
      if (this.$refs.editor) this.$refs.editor.resetView()
    },
    // P41 BT-53: a colour / width chosen in the toolbar's pen panel.
    onPenStyle(style) {
      this.penStyle = style
    },
    // P41 BT-54: «پاک کردن همهٔ تخته», already confirmed in the toolbar. The
    // editor removes everything as one undoable step; the autosave saves it.
    onClearBoard() {
      if (this.$refs.editor) this.$refs.editor.clearBoard()
    },
    // ---- equation (as the Workspace) ----------------------------------------
    onEquationSelection(id) {
      this.selectedEquationId = id || null
    },
    onEquationTool() {
      const equation = this.equationOf(this.selectedEquationId)
      if (equation) this.openEquationDialog('edit', equation)
      else this.openEquationDialog('insert', null)
    },
    onEquationEdit(id) {
      const equation = this.equationOf(id)
      if (equation) this.openEquationDialog('edit', equation)
    },
    equationOf(id) {
      const editor = this.$refs.editor
      return id && editor ? editor.getEquation(id) : null
    },
    openEquationDialog(mode, equation) {
      if (!this.boardEditable || this.equationDialog) return
      this.equationDialog = {
        mode,
        id: equation ? equation.id : null,
        source: equation ? equation.latex : '',
        busy: false,
        problem: null,
      }
    },
    closeEquationDialog() {
      this.equationDialog = null
    },
    onEquationEdited() {
      const dialog = this.equationDialog
      if (!dialog) return
      if (dialog.problem !== 'gone' && dialog.problem !== 'readOnly') {
        dialog.problem = null
      }
    },
    async onEquationSubmit(latex) {
      const dialog = this.equationDialog
      const editor = this.$refs.editor
      if (!dialog || dialog.busy) return
      if (!editor || !this.boardEditable) {
        dialog.problem = 'readOnly'
        return
      }
      if (dialog.mode === 'edit' && !editor.getEquation(dialog.id)) {
        dialog.problem = 'gone'
        return
      }
      if (dialog.mode === 'edit' && latex === dialog.source) {
        this.equationDialog = null
        this.focusEditor()
        return
      }
      dialog.busy = true
      let done = null
      let problem = null
      try {
        done =
          dialog.mode === 'edit'
            ? await editor.updateEquation({ id: dialog.id, latex })
            : await editor.insertEquation({ latex })
      } catch (err) {
        problem = EQUATION_PROBLEMS[err && err.code] || 'invalid'
      }
      if (this.destroyed || this.equationDialog !== dialog) return
      dialog.busy = false
      if (done) {
        this.equationDialog = null
        this.focusEditor()
        return
      }
      if (problem) dialog.problem = problem
      else if (dialog.mode === 'edit' && !editor.getEquation(dialog.id)) {
        dialog.problem = 'gone'
      } else dialog.problem = 'readOnly'
    },
    focusEditor() {
      this.$nextTick(() => {
        if (this.$refs.editor) this.$refs.editor.focus()
      })
    },
    // ---- share (WB-M2A, as the Workspace) -----------------------------------
    async onShareTool() {
      const editor = this.$refs.editor
      if (this.shareDisabled || !editor) return
      this.shareRendering = true
      const seq = this.initSeq
      let rendered = null
      let failed = false
      try {
        rendered = await editor.renderSnapshot()
      } catch (err) {
        failed = true
      }
      this.shareRendering = false
      if (this.destroyed || seq !== this.initSeq) return
      if (failed) {
        this.shareDialog = { share: null, busy: false, problem: 'render' }
        return
      }
      if (!rendered) {
        this.shareDialog = { share: null, busy: false, problem: 'empty' }
        return
      }
      const boardState = this.$store.state.aiTutorBoard
      const share = freezeBoardShare({
        scope: rendered.scope,
        elementIds: rendered.elementIds,
        bounds: rendered.bounds,
        selectedCount: rendered.selectedCount,
        blob: rendered.blob,
        width: rendered.width,
        height: rendered.height,
        bytes: rendered.bytes,
        boardId: boardState.boardId,
        boardVersion: boardState.version,
        sceneVersion: rendered.sceneVersion,
        previewUrl: createPreviewUrl(rendered.blob),
      })
      this.shareDialog = {
        share,
        busy: false,
        problem: null,
        handedOver: false,
      }
    },
    closeShareDialog() {
      const dialog = this.shareDialog
      if (!dialog) return
      if (dialog.share && dialog.share.previewUrl && !dialog.handedOver) {
        revokePreviewUrl(dialog.share.previewUrl)
      }
      this.shareDialog = null
    },
    // The Workspace's send, unchanged: flush first (a stale board blocks), the
    // chat's gates, then the ordinary chat send with the frozen picture.
    async onShareSend({ text, intent }) {
      const dialog = this.shareDialog
      if (!dialog || dialog.busy || !dialog.share || this.shareBlockedBy) return
      dialog.busy = true
      dialog.problem = null
      const seq = this.initSeq
      const saved = await this.$store.dispatch('aiTutorBoard/flush')
      if (
        this.destroyed ||
        seq !== this.initSeq ||
        this.shareDialog !== dialog
      ) {
        return
      }
      if (this.saveState === 'stale') {
        dialog.busy = false
        dialog.problem = 'stale'
        return
      }
      const blocked = this.shareBlockedBy
      if (blocked) {
        dialog.busy = false
        dialog.problem = blocked
        return
      }
      const share = dialog.share
      const message = typeof text === 'string' ? text.trim() : ''
      if (!message) {
        dialog.busy = false
        return
      }
      const boardVersion = saved ? this.$store.state.aiTutorBoard.version : null
      const request = boardShareRequest(intent)
      dialog.handedOver = true
      this.$store.dispatch('aiTutor/sendMessage', {
        text: message,
        image: {
          file: share.blob,
          fileFormat: 'png',
          previewUrl: share.previewUrl,
          source: 'board',
        },
        request: request || undefined,
        boardShare: toBoardShareDescriptor(share, boardVersion),
      })
      this.shareDialog = null
      // A Board that could not be saved stays open with its NOT SAVED notice
      // and retry, as the Workspace does -- closing now would drop the unsaved
      // strokes (focused review P2). The picture is on its way in the chat.
      if (!saved && this.$store.getters['aiTutorBoard/hasUnsavedChanges']) {
        return
      }
      // Saved: the conversation shows the picture on its way -- back to it.
      this.closing = true
      this.$emit('close', { sent: true })
    },
    onRetrySave() {
      this.$store.dispatch('aiTutorBoard/retrySave')
    },
    async onReload() {
      if (this.reloading) return
      this.reloading = true
      const seq = this.initSeq
      try {
        const result = await this.$store.dispatch('aiTutorBoard/reload')
        if (this.destroyed || seq !== this.initSeq) return
        if (result && result.ok) this.mountScene(result.scene)
      } finally {
        this.reloading = false
      }
    },
    onPageHide() {
      this.$store.dispatch('aiTutorBoard/flushOnPageHide')
    },
    // Focus never wanders to the chat behind the sheet (its own dialogs are
    // inside this element).
    onFocusIn(event) {
      if (this.destroyed || !event || !event.target) return
      if (this.$el.contains(event.target)) return
      if (this.$refs.close) this.$refs.close.focus()
    },
    // The backdrop closes only a press that STARTED on it: a stroke drawn on
    // the Board and released over the backdrop is not a close (the editor
    // does not capture the pointer).
    onSheetPointerDown(event) {
      this.backdropPressed = !!event && event.target === this.$el
    },
    onBackdropClick() {
      const pressed = this.backdropPressed
      this.backdropPressed = false
      if (pressed) this.requestClose()
    },
    // ×, the backdrop: the Board is saved first; one that cannot be saved is
    // never left silently.
    async requestClose() {
      if (
        this.closing ||
        this.shareDialog ||
        this.equationDialog ||
        this.leavePrompt
      ) {
        return
      }
      this.closing = true
      const saved = await this.$store.dispatch('aiTutorBoard/flush')
      if (this.destroyed) return
      if (!saved && this.$store.getters['aiTutorBoard/hasUnsavedChanges']) {
        // P40 QA-28: asked in the sheet (stay is the default), not with
        // window.confirm.
        this.closing = false
        this.leavePrompt = true
        this.$nextTick(() => {
          if (this.$refs.leaveStay) this.$refs.leaveStay.focus()
        })
        return
      }
      this.$emit('close', { sent: false })
    },
    onLeaveStay() {
      this.leavePrompt = false
    },
    onLeaveAnyway() {
      this.leavePrompt = false
      this.closing = true
      this.$emit('close', { sent: false })
    },
    // Unsaved work (waiting on the retry ladder, given up, or not yet sent)
    // is saved now rather than at the next retry, or never.
    onReconnect() {
      if (this.destroyed) return
      if (['failed', 'retrying', 'dirty'].includes(this.saveState)) {
        this.$store.dispatch('aiTutorBoard/retrySave')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// The rendered V405 sheet (`.borna-whiteboard-sheet` and its panel): a
// blurred backdrop, a 1080 x 860 panel (92vh at most) with a 28px radius on
// a desktop, the whole screen up to 900px. Above the app's own layers
// (bottom navigation 40), below its dialogs (160) -- the Board's equation
// and share dialogs open over it.
.chat-board-sheet {
  position: fixed;
  inset: 0;
  z-index: 150;
  display: grid;
  place-items: center;
  padding: 18px;
  background: rgba(10, 19, 49, 0.42);
  backdrop-filter: blur(6px);
  font-family: $borna-font-family;
  color: $borna-text;
  overscroll-behavior: contain;
}

.chat-board-sheet__panel {
  display: grid;
  // One column that may shrink below its content: the Board toolbar then
  // scrolls inside its own bar on a phone instead of leaving the panel.
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto minmax(0, 1fr) auto;
  gap: 12px;
  box-sizing: border-box;
  width: min(100%, 1080px);
  height: min(92vh, 860px);
  padding: 16px;
  overflow: hidden;
  border: 1px solid #dce5f8;
  border-radius: 28px;
  background: #f8fbff;
  box-shadow: 0 30px 90px rgba(8, 18, 52, 0.28);
}

.chat-board-sheet__head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 4px 4px 0;
}

.chat-board-sheet__heading {
  flex: 1;
  min-width: 0;

  h3 {
    margin: 0;
    color: $borna-ink;
    font-size: 18px;
    line-height: 1.5;
  }

  p {
    margin: 3px 0 0;
    color: $borna-muted;
    font-size: 12px;
    line-height: 1.9;
  }
}

.chat-board-sheet__save {
  flex: none;
  align-self: center;
  padding: 3px 10px;
  border-radius: $borna-radius-pill;
  background: $borna-canvas;
  color: $borna-muted;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;

  &--saved {
    background: $borna-teal-50;
    color: $borna-teal-700;
  }

  &--failed,
  &--stale {
    background: $borna-error-bg;
    color: $borna-error-text;
  }
}

.chat-board-sheet__close {
  display: grid;
  place-items: center;
  flex: none;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 1px solid $borna-border;
  border-radius: 14px;
  background: #fff;
  color: $borna-muted;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid #61dce8;
    outline-offset: 2px;
  }
}

// The production Board inside the V405 stage frame.
.chat-board-sheet__stage {
  position: relative;
  min-height: 0;
  overflow: hidden;
  border: 1px solid #dce5f8;
  border-radius: 24px;
  background: #fff;
}

.chat-board-sheet__empty {
  position: absolute;
  inset: 0;
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 6px;
  padding: 24px;
  color: $borna-muted;
  font-size: 13px;
  text-align: center;
  pointer-events: none;

  strong {
    color: $borna-ink;
    font-size: 17px;
    font-weight: 500;
  }
}

.chat-board-sheet__foot {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 36px;
  padding: 0 2px 2px;
  color: $borna-muted;
  font-size: 11px;
  line-height: 1.9;
}

.chat-board-sheet__hint {
  flex: 1;
  min-width: 0;
}

.chat-board-sheet__notice {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: $borna-error-text;
  font-weight: 700;
}

.chat-board-sheet__notice--leave {
  flex-wrap: wrap;

  > span {
    flex: 1 1 12em;
  }
}

.chat-board-sheet__equation-edit {
  margin-inline-start: auto;
}

.chat-board-sheet__button {
  flex: none;
  height: 32px;
  padding: 0 14px;
  border: 1px solid $borna-indigo-100;
  border-radius: 10px;
  background: $borna-indigo-50;
  color: $borna-indigo-900;
  font-family: inherit;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.chat-board-sheet__state {
  display: grid;
  grid-row: 2 / span 3;
  place-content: center;
  justify-items: center;
  gap: 12px;
  padding: 24px;
  color: $borna-muted;
  font-size: 13px;
  text-align: center;

  p {
    margin: 0;
  }
}

// V405 `@media (max-width: 900px)`: the whole screen, no radius.
@media (max-width: 900px) {
  .chat-board-sheet {
    padding: 0;
  }

  .chat-board-sheet__panel {
    width: 100%;
    height: 100vh;
    height: 100dvh;
    max-height: 100dvh;
    padding: 12px;
    border-radius: 0;
  }

  .chat-board-sheet__heading {
    h3 {
      font-size: 16px;
    }

    p {
      font-size: 11px;
    }
  }

  .chat-board-sheet__stage {
    border-radius: 18px;
  }
}
</style>
